import { useCallback, useEffect, useMemo, useState } from "react";
import TicklersTypeService from "../../../../../../../services/administration/ticklers";
import {
  ITicklerExtended,
  ITicklerTypeExtended,
} from "../../../../../../../types/Tickler";
import TicklersService from "../../../../../../../services/accounts/ticklers";
import dayjs from "dayjs";
import { IOption } from "../../../../../../../types/Option";

type TInitialValuesProps = {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  id: string | null;
};

export const useGetInitialValues = ({
  setIsLoading,
  id,
}: TInitialValuesProps) => {
  const [types, setTypes] = useState<ITicklerTypeExtended[]>();

  const [options, setOptions] = useState<{ value: string; label: string }[]>();

  const [entry, setEntry] = useState<ITicklerExtended | null>(null);

  const [isTickleTypesLoading, setIsTicklerTypesLoading] = useState<boolean>(false);

  const request = useMemo(
    () => ({
      pageNumber: 1,
      pageSize: 10000,
      order: 2
    }),
    []
  );

  const fetchTicklerTypes = useCallback(() => {
    setIsTicklerTypesLoading(true);
    TicklersTypeService.fetchAll(request)
      .then((res: any) => {
        const result: IOption[] = res.map((record: any) => ({
          label: record.typeName,
          value: record.id,
        }));
        setTypes(res);
        setOptions(result);
        setIsTicklerTypesLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [setIsLoading, request]);

  const fetchTickler = useCallback(() => {
    if (id !== null) {
      setIsLoading(true);
      TicklersService.fetch(id)
        .then((res: any) => {
          setEntry(res);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  }, [id, setIsLoading]);

  useEffect(() => {
    fetchTickler();
    fetchTicklerTypes();
  }, [id, fetchTickler, fetchTicklerTypes]);

  const initialValues = useMemo(() => {
    return entry !== null
      ? {
          date: dayjs(entry.date),
          ticklerTypeId: entry.ticklerTypeId,
          text: entry.text,
          paymentAlert: entry.paymentAlert,
        }
      : {
          date: dayjs(),
          ticklerTypeId: options ? options[0].value : "",
          text: types ? types[0].defaultText : "",
          paymentAlert: false,
        };
  }, [entry, options, types]);

  return { options, types, initialValues, isTickleTypesLoading };
};
